/**
 * `SkillAssessmentTable` — tabella condivisa delle skill con i voti (Self /
 * Reviews / AI) e l'autovalutazione **al volo** del Self.
 *
 * È il componente unico usato sia dalla pagina "Le mie skill" (`SkillList`) sia
 * dal "Dettaglio competenza" (`CompetenceDetail`): entrambe mostrano la stessa
 * tabella, con lo stesso inline edit, e differiscono solo per le righe passate e
 * per eventuali colonne extra (es. il "Peso" della skill sulla competenza).
 *
 * Comportamento (allineato ai Req. 8.2, 8.4, 8.5, 8.6):
 *  - colonne: Skill [+ colonne extra] + Livello (Self) + Esperienza +
 *    Propensione + Score + Azioni;
 *  - i valori sono in sola lettura; l'assenza è "—", il `0` (per le stelle) è un
 *    voto valido distinto dall'assenza;
 *  - lo **Score** mostrato è lo Score_Skill derivato dall'API (`row.score`): il
 *    Frontend NON ricalcola lo score con pesi hardcoded (Req. 8.5);
 *  - la colonna **Livello** mostra il Livello_Self come badge affiancato — se
 *    esiste più di una Fonte di Livello — da un contatore cliccabile: apre lo
 *    schemino ({@link LevelSourcesModal}) con tutti i Livelli per fonte (il
 *    proprio, quello dell'AI e quelli delle revisioni Reviewer/Manager). Le
 *    colonne AI e Reviews non sono più mostrate autonomamente;
 *  - ordinamento client‑side per colonna, con i valori assenti (`null`) sempre
 *    in coda a prescindere dalla direzione;
 *  - inline edit del Self attivato dal pulsante di riga: mostra un picker con il
 *    Livello (obbligatorio, Req. 8.6), gli anni di esperienza (opzionale) e la
 *    Propensione (opzionale, dato informativo). Il
 *    salvataggio è **esplicito**: un pulsante Salva (attivo solo con il Livello
 *    valorizzato) precede il pulsante Chiudi. Finché manca il Livello il
 *    relativo select è bordato di rosso.
 *  - salvataggio via `PUT /api/assessments/me/:skillId/self`, con invalidazione
 *    della query `['assessments','me']`.
 *
 * Il componente gestisce internamente lo stato di editing e la mutation; le
 * pagine restano responsabili del recupero dati, del filtro e del layout.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentView,
  ExperienceRange,
  Livello,
  ScoringConfigDto,
  SetScoreRequest,
  SkillDto,
} from '../shared/dto';
import {
  DEFAULT_SCORING_CONFIG,
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  livelloRank,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ExperienceRangeSelect } from './ExperienceRangeSelect';
import { LivelloBadge } from './LivelloBadge';
import { LivelloGuide } from './LivelloGuide';
import { LivelloSelect } from './LivelloSelect';
import { ScoreCalcGuide } from './ScoreCalcGuide';
import { ScoreDetailModal } from './ScoreDetailModal';
import { LevelSourcesModal } from './LevelSourcesModal';
import { levelSourcesCount } from './skillLevelSources';
import { ABSENT_INDICATOR } from './ScoreTriple';
import { ScoreBadge } from './ScoreBadge';
import { CertificationCountBadge } from './CertificationCountBadge';
import { PropensionSelect, propensionLabel } from './PropensionSelect';
import {
  Button,
  DataTable,
  Icon,
  InfoHint,
  type Column,
  type SortDirection,
  type SortState,
} from './ui';

/**
 * Rango di Livello aggregato delle recensioni: media semplice dei ranghi
 * reviewer/manager **presenti** (`[1,5]` continuo), `null` se nessuna delle due
 * fonti ha votato. Non applica pesi hardcoded (Req. 8.5): i pesi delle fonti
 * vivono nel motore di scoring dell'API. È usato per l'ordinamento e per
 * scegliere il badge di Livello da mostrare nella colonna "Reviews".
 */
export function reviewsLevelRank(row: AssessmentView): number | null {
  const ranks: number[] = [];
  if (row.reviewerLevelRank !== null) ranks.push(row.reviewerLevelRank);
  if (row.managerLevelRank !== null) ranks.push(row.managerLevelRank);
  if (ranks.length === 0) return null;
  return ranks.reduce((sum, r) => sum + r, 0) / ranks.length;
}

/**
 * Comparatore per la colonna "Score": ordina per Score_Skill derivato
 * dall'API (`row.score`), con i valori assenti (`null`) **sempre in coda**,
 * coerente con le altre colonne.
 */
function compareScore(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.score;
  const vb = b.score;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Confronto lessicografico deterministico dei nomi skill (già orientato). */
function compareSkillName(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  if (a.skillName < b.skillName) return -sign;
  if (a.skillName > b.skillName) return sign;
  return 0;
}

/**
 * Comparatore per la colonna "Propensione": ordina per voto informativo
 * (`selfInterest`), con i valori assenti (`null`) **sempre in coda**. Il `0` è
 * un voto valido, distinto dall'assenza. `DataTable` non applica la direzione
 * ai `sortComparator`: il segno restituito è quello finale.
 */
function compareInterest(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.selfInterest;
  const vb = b.selfInterest;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/**
 * Comparatore per la colonna "Livello (Self)": ordina per rango della scala
 * (`e-1..e-5`), con i livelli assenti (`null`) **sempre in coda**.
 */
function makeLivelloComparator() {
  return (a: AssessmentView, b: AssessmentView, direction: SortDirection): number => {
    const la = a.livelloSelf;
    const lb = b.livelloSelf;
    if (la === null && lb === null) return 0;
    if (la === null) return 1;
    if (lb === null) return -1;
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (livelloRank(la) - livelloRank(lb));
  };
}

/**
 * Comparatore per la colonna "Esperienza": ordina per rango della scala di
 * fasce (`lt-1..gt-10`), con le fasce assenti (`null`) **sempre in coda**.
 */
function makeExperienceComparator() {
  const rankOf = (value: AssessmentView['yearsOfExperience']) =>
    value === null ? -1 : EXPERIENCE_RANGE_VALUES.indexOf(value);
  return (a: AssessmentView, b: AssessmentView, direction: SortDirection): number => {
    const ra = a.yearsOfExperience;
    const rb = b.yearsOfExperience;
    if (ra === null && rb === null) return 0;
    if (ra === null) return 1;
    if (rb === null) return -1;
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (rankOf(ra) - rankOf(rb));
  };
}

/**
 * Rende la Propensione come barra di avanzamento (`progress`) sulla scala
 * `0..10`: la larghezza del riempimento è proporzionale al voto e l'etichetta
 * testuale è esposta come tooltip e testo accessibile. Se il valore è assente
 * (`null`) mostra l'indicatore di assenza "—". Dato puramente informativo
 * (Req. 8.2).
 */
function propensionCell(value: number | null, label: string) {
  if (value === null) {
    return (
      <span className="skill-list__absent" aria-label={`${label}: nessuna preferenza`}>
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const percent = (value / 10) * 100;
  const text = propensionLabel(value);
  return (
    <span
      className="skill-list__propension-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={10}
      aria-valuenow={value}
      aria-valuetext={text}
      aria-label={label}
      title={`${text} (${value}/10)`}
    >
      <span
        className="skill-list__propension-bar-fill"
        style={{ width: `${percent}%` }}
      />
    </span>
  );
}

/**
 * Rende la colonna "Livello" (Self): il badge del Livello auto‑dichiarato,
 * affiancato — quando esiste più di una Fonte di Livello (Self, AI, revisioni)
 * — da un contatore cliccabile con il numero totale di fonti. Cliccando il
 * contatore si apre lo schemino con tutti i Livelli per fonte
 * ({@link LevelSourcesModal}), analogamente al badge dello Score.
 */
function levelSelfCell(row: AssessmentView, onShowSources?: () => void) {
  const sources = levelSourcesCount(row);
  // Il contatore compare solo quando c'è più di una fonte di Livello: con una
  // sola fonte non aggiungerebbe informazione (basta il badge già mostrato).
  const showCounter = sources > 1 && onShowSources !== undefined;
  const counterLabel = `${sources} fonti di Livello — mostra il dettaglio`;
  return (
    <span className="skill-list__level-cell">
      <LivelloBadge livello={row.livelloSelf} />
      {showCounter && (
        <button
          type="button"
          className="skill-list__level-sources-count"
          onClick={onShowSources}
          title={counterLabel}
          aria-label={counterLabel}
        >
          {sources}
        </button>
      )}
    </span>
  );
}

/**
 * Rende lo "Score" (Score_Skill derivato dall'API) come badge colorato, o "—"
 * quando assente. Se `onClick` è fornito e lo Score è presente, il badge diventa
 * un pulsante che apre il dettaglio del calcolo.
 */
function scoreBadgeCell(row: AssessmentView, onClick?: () => void) {
  const value = row.score;
  // Badge del numero di certificazioni valide che contribuiscono allo Score
  // (coccarda + numero), affiancato al badge dello Score quando presenti.
  const certBadge = (
    <CertificationCountBadge
      count={row.certificationCount}
      skillName={row.skillName}
    />
  );
  if (value === null) {
    return (
      <span className="skill-list__score-cell">
        <span className="skill-list__absent" aria-label="Score: assente">
          {ABSENT_INDICATOR}
        </span>
        {certBadge}
      </span>
    );
  }
  if (!onClick) {
    return (
      <span className="skill-list__score-cell">
        <ScoreBadge value={value} label="Score" />
        {certBadge}
      </span>
    );
  }
  const scoreLabel =
    value > 10
      ? `Score oltre 10 (${value.toFixed(1)})`
      : `Score ${value.toFixed(1)} su 10`;
  return (
    <span className="skill-list__score-cell">
      <button
        type="button"
        className="score-badge-button"
        onClick={onClick}
        title="Mostra come è calcolato lo Score"
        aria-label={`${scoreLabel} — mostra il dettaglio del calcolo`}
      >
        <ScoreBadge value={value} label="Score" />
      </button>
      {certBadge}
    </span>
  );
}

export interface SkillAssessmentTableProps {
  /** Righe da mostrare (già filtrate/preparate dalla pagina). */
  rows: readonly AssessmentView[];
  /** Chiave query da invalidare al salvataggio (default: `['assessments','me']`). */
  invalidateKey?: readonly unknown[];
  /**
   * Chiavi query aggiuntive da invalidare al salvataggio, oltre a `invalidateKey`.
   * Utile quando la pagina mostra dati derivati da altre query (es. il donut del
   * rank in `CompetenceDetail`, alimentato da `['dashboard','me']`).
   */
  additionalInvalidateKeys?: readonly (readonly unknown[])[];
  /**
   * `true` se l'AI è abilitata per l'utente: mostra la colonna del Livello AI e
   * il tasto "Valuta con AIgor" (Req. 8.4).
   */
  aiEnabled?: boolean;
  /**
   * Mappa `skillId → SkillDto` per determinare `selfAssessable`/`aiAssessable`.
   * Le skill assenti dalla mappa sono trattate come abilitate (legacy).
   */
  skillById?: Map<string, SkillDto>;
  /**
   * Callback per aprire il Drawer di AIgor su una skill. Se assente, il tasto
   * AIgor non viene mostrato (a prescindere da `aiEnabled`).
   */
  onOpenAigor?: (row: AssessmentView) => void;
  /**
   * Colonne extra iniettate **subito prima** della colonna Livello (es. "Peso"
   * della skill sulla competenza). In modifica sono coperte dal picker.
   */
  extraColumns?: Column<AssessmentView>[];

  /* --- Passthrough a DataTable ------------------------------------- */
  loading?: boolean;
  error?: string | null;
  emptyMessage?: React.ReactNode;
  defaultSort?: SortState | null;
  paginated?: boolean;
  pageSize?: number;
}

/**
 * Tabella skill + inline edit del Self, condivisa tra "Le mie skill" e
 * "Dettaglio competenza".
 */
export function SkillAssessmentTable({
  rows,
  invalidateKey = ['assessments', 'me'],
  additionalInvalidateKeys = [],
  aiEnabled = false,
  skillById,
  onOpenAigor,
  extraColumns = [],
  loading,
  error,
  emptyMessage,
  defaultSort = { key: 'skillName', direction: 'asc' },
  paginated = true,
  pageSize = 10,
}: SkillAssessmentTableProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  // Riga di cui è aperto il dettaglio di calcolo dello Score (o `null`).
  const [scoreDetailRow, setScoreDetailRow] = useState<AssessmentView | null>(null);

  // Riga di cui è aperto lo schemino con tutti i Livelli per fonte (o `null`).
  const [levelSourcesRow, setLevelSourcesRow] = useState<AssessmentView | null>(null);

  // Configurazione di scoring (pesi fonti/assi, `minScore`): serve al modale di
  // dettaglio per ricostruire i passaggi del calcolo. Leggibile da qualsiasi
  // utente autenticato; in attesa/errore ripiega sui default applicativi.
  const scoringConfigQuery = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });
  const scoringConfig = scoringConfigQuery.data ?? DEFAULT_SCORING_CONFIG;

  // Salvataggio dell'autovalutazione al volo: stesso endpoint del wizard
  // (PUT /api/assessments/me/:skillId/self). Il corpo invia il Livello
  // (obbligatorio), la Propensione (opzionale, informativa) e la
  // fascia di esperienza (opzionale). Al successo invalida la lista.
  const saveSelfAssessment = useMutation({
    mutationFn: async ({
      skillId,
      livello,
      interest,
      yearsOfExperience,
    }: {
      skillId: string;
      livello: Livello;
      interest: number | null;
      yearsOfExperience: ExperienceRange | null;
    }) => {
      const body: SetScoreRequest = { livello, interest, yearsOfExperience };
      await api.put<void>(`/api/assessments/me/${encodeURIComponent(skillId)}/self`, body);
    },
    onSuccess: () => {
      // Invalida la lista principale e le eventuali query aggiuntive dipendenti
      // (es. il dashboard che alimenta il donut del rank in CompetenceDetail).
      void queryClient.invalidateQueries({ queryKey: invalidateKey as unknown[] });
      for (const key of additionalInvalidateKeys) {
        void queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
    },
  });

  // Skill attualmente in salvataggio: disabilita solo la riga interessata.
  const savingSkillId = saveSelfAssessment.isPending
    ? saveSelfAssessment.variables?.skillId ?? null
    : null;

  // Inline edit: skill in modifica (o `null`) e bozze di Livello, Propensione e
  // anni. Il salvataggio è esplicito (tasto Salva): le bozze si
  // limitano a raccogliere le scelte, senza inviare nulla finché l'utente non
  // conferma.
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [draftLivello, setDraftLivello] = useState<Livello | null>(null);
  const [draftInterest, setDraftInterest] = useState<number | null>(null);
  const [draftYears, setDraftYears] = useState<ExperienceRange | null>(null);

  const startEditing = (row: AssessmentView) => {
    setEditingSkillId(row.skillId);
    setDraftLivello(row.livelloSelf);
    setDraftInterest(row.selfInterest);
    setDraftYears(row.yearsOfExperience);
    saveSelfAssessment.reset();
  };

  const cancelEditing = () => {
    setEditingSkillId(null);
    setDraftLivello(null);
    setDraftInterest(null);
    setDraftYears(null);
  };

  // Aggiornamento delle bozze: nessun invio automatico, solo stato locale.
  // Livello "Nessuno" (`e-0`): la competenza è assente, quindi gli anni di
  // esperienza non hanno significato; azzeriamo la bozza degli anni (il select
  // Esperienza è anche disabilitato lato render finché il Livello resta `e-0`).
  const handleLivello = (livello: Livello) => {
    setDraftLivello(livello);
    if (livello === 'e-0') setDraftYears(null);
  };
  const handleYears = (years: ExperienceRange | null) => setDraftYears(years);

  // Salvataggio esplicito (tasto Salva): richiede solo il Livello (Req. 8.6);
  // Propensione e anni sono opzionali. PUT e chiusura del picker al
  // successo.
  const handleSave = (skillId: string) => {
    if (draftLivello === null) return;
    saveSelfAssessment.mutate(
      {
        skillId,
        livello: draftLivello,
        interest: draftInterest ?? null,
        yearsOfExperience: draftYears,
      },
      { onSuccess: () => cancelEditing() },
    );
  };

  const selfAssessableOf = (skillId: string) =>
    skillById?.get(skillId)?.selfAssessable ?? true;
  const aiAssessableOf = (skillId: string) =>
    skillById?.get(skillId)?.aiAssessable ?? true;
  const descriptionOf = (skillId: string) => skillById?.get(skillId)?.description;

  const columns = useMemo<Column<AssessmentView>[]>(() => {
    const isEditing = (skillId: string) => editingSkillId === skillId;

    // In modifica la cella Skill copre Livello + eventuali colonne extra + gli
    // altri valori + Score, così il picker compare al loro posto. La colonna
    // Azioni resta scoperta per il pulsante Annulla.
    // Colonne coperte: Skill(1) + extra(n) + Livello(1) + Esperienza(1) +
    // Propensione(1) + Score(1). Le colonne AI e Reviews non esistono più: i
    // rispettivi Livelli sono ora consultabili dallo schemino aperto dal
    // contatore accanto al badge del Livello.
    const editingSpan = 5 + extraColumns.length;

    const renderPicker = (row: AssessmentView) => {
      const isSaving = savingSkillId === row.skillId;
      // Finché manca il Livello (Req. 8.6) il select è bordato di rosso.
      const needsLevel = draftLivello === null;
      // Con Livello "Nessuno" (e-0) gli anni di esperienza non hanno significato:
      // il valore mostrato è forzato a vuoto (la bozza è già azzerata in
      // handleLivello) e il select è disabilitato.
      const yearsValue = draftLivello === 'e-0' ? null : draftYears;
      const levelClasses = [
        'user-admin__select',
        'skill-list__picker-level',
        needsLevel ? 'skill-list__picker-level--invalid' : '',
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <div className="skill-list__picker">
          <span className="skill-list__picker-name">{row.skillName}</span>
          {/* Livello prima del punteggio, con lo stile "pill" degli altri select. */}
          <LivelloGuide className="skill-list__picker-guide" />
          <span className={levelClasses} data-empty={draftLivello === null || undefined}>
            <LivelloSelect
              value={draftLivello}
              label={`Livello di ${row.skillName}`}
              disabled={isSaving}
              onChange={handleLivello}
            />
          </span>
          {/* Anni di esperienza: dato opzionale e informativo, tra il Livello e
              le stelle. Con Livello "Nessuno" (e-0) la competenza è assente: il
              select è disabilitato e mostrato vuoto (la bozza degli anni è
              azzerata in handleLivello). */}
          <span
            className="user-admin__select skill-list__picker-years"
            data-empty={yearsValue === null || undefined}
          >
            <ExperienceRangeSelect
              value={yearsValue}
              label={`Anni di esperienza di ${row.skillName}`}
              disabled={isSaving || draftLivello === 'e-0'}
              onChange={handleYears}
            />
          </span>
          {/* Propensione: voto opzionale e informativo. */}
          <span
            className="user-admin__select skill-list__picker-propension"
            data-empty={draftInterest === null || undefined}
          >
            <PropensionSelect
              value={draftInterest}
              label={`Propensione ${row.skillName}`}
              disabled={isSaving}
              onChange={setDraftInterest}
            />
          </span>
        </div>
      );
    };

    const renderActions = (row: AssessmentView) => {
      if (isEditing(row.skillId)) {
        const isSaving = savingSkillId === row.skillId;
        // Salvataggio esplicito: attivo solo con il Livello valorizzato (Req.
        // 8.6); Propensione e anni sono opzionali. Il tasto Salva
        // precede il tasto Chiudi.
        const canSave = draftLivello !== null && !isSaving;
        return (
          <div className="admin-item__actions">
            <Button
              type="button"
              size="sm"
              iconOnly
              variant="primary"
              disabled={!canSave}
              aria-label={`Salva la valutazione di ${row.skillName}`}
              title="Salva"
              onClick={() => handleSave(row.skillId)}
            >
              <Icon name="check" />
            </Button>
            <Button
              type="button"
              size="sm"
              iconOnly
              disabled={isSaving}
              aria-label={`Annulla la valutazione di ${row.skillName}`}
              title="Annulla"
              onClick={cancelEditing}
            >
              <Icon name="close" />
            </Button>
          </div>
        );
      }
      const selfAssessable = selfAssessableOf(row.skillId);
      const aiAssessable = aiAssessableOf(row.skillId);
      return (
        <div className="admin-item__actions">
          {/* Tasto Valuta/Modifica (self): solo se la skill è autovalutabile. */}
          {selfAssessable && (
            <Button
              type="button"
              size="sm"
              iconOnly
              aria-label={
                row.livelloSelf === null
                  ? `Valuta ${row.skillName}`
                  : `Modifica la valutazione di ${row.skillName}`
              }
              title={row.livelloSelf === null ? 'Valuta' : 'Modifica'}
              onClick={() => startEditing(row)}
            >
              <Icon name="edit" />
            </Button>
          )}
          {/* "Valuta con AIgor": solo se abilitata (Req. 8.4), la skill è
              valutabile con AI e la pagina fornisce la callback. */}
          {aiEnabled && aiAssessable && onOpenAigor && (
            <Button
              type="button"
              size="sm"
              iconOnly
              className="ui-btn--ai"
              aria-label={`Valuta con AIgor ${row.skillName}`}
              title="Valuta con AIgor"
              onClick={() => onOpenAigor(row)}
            >
              <span className="ui-btn__ai-label">AI</span>
            </Button>
          )}
        </div>
      );
    };

    return [
      {
        key: 'skillName',
        header: 'Skill',
        sortable: true,
        sortComparator: compareSkillName,
        cellSpan: (row) => (isEditing(row.skillId) ? editingSpan : 1),
        render: (row) =>
          isEditing(row.skillId) ? (
            renderPicker(row)
          ) : (
            <span className="skill-list__skill-cell">
              <span className="skill-list__skill-name">{row.skillName}</span>
              <InfoHint
                className="skill-list__skill-info"
                text={descriptionOf(row.skillId)}
                label={`Descrizione di ${row.skillName}`}
              />
            </span>
          ),
      },
      // Colonne extra (es. "Peso" nel dettaglio competenza) prima di "Livello".
      ...extraColumns,
      {
        key: 'livello',
        header: (
          <span className="score-calc-guide__header">
            Livello
            <LivelloGuide className="score-calc-guide__trigger" />
          </span>
        ),
        sortable: true,
        sortComparator: makeLivelloComparator(),
        // Livello dichiarato in autovalutazione ("—" se assente), affiancato dal
        // contatore delle Fonti di Livello: cliccandolo si apre lo schemino con
        // tutti i Livelli (Self, AI, revisioni).
        render: (row) => levelSelfCell(row, () => setLevelSourcesRow(row)),
        className: 'skill-list__level-col',
      },
      {
        key: 'experience',
        header: 'Esperienza',
        sortable: true,
        sortComparator: makeExperienceComparator(),
        // Fascia di anni di esperienza dichiarata in autovalutazione, in sola
        // lettura ("—" se assente). Dato opzionale e puramente informativo.
        render: (row) =>
          row.yearsOfExperience !== null ? (
            <span className="skill-list__years">
              {EXPERIENCE_RANGE_LABELS[row.yearsOfExperience]}
            </span>
          ) : (
            <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
              {ABSENT_INDICATOR}
            </span>
          ),
        className: 'skill-list__experience-col',
      },
      {
        key: 'interest',
        header: 'Propensione',
        sortable: true,
        sortComparator: compareInterest,
        // Propensione (voto informativo) come etichetta testuale: "—" se assente.
        render: (row) => propensionCell(row.selfInterest, 'Propensione'),
        className: 'skill-list__score-col skill-list__score-col--self',
      },
      {
        key: 'score',
        header: (
          <span className="score-calc-guide__header">
            Score
            <ScoreCalcGuide />
          </span>
        ),
        sortable: true,
        sortComparator: compareScore,
        // Score_Skill derivato dall'API: il Frontend non lo ricalcola (Req. 8.5).
        // Il badge è cliccabile e apre il dettaglio del calcolo per la riga.
        render: (row) =>
          scoreBadgeCell(row, () => setScoreDetailRow(row)),
        className: 'skill-list__score-col',
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (row) => renderActions(row),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingSkillId, savingSkillId, aiEnabled, onOpenAigor, skillById, extraColumns, draftInterest, draftLivello, draftYears]);

  return (
    <>
      {saveSelfAssessment.isError && (
        <p className="skill-list__save-error" role="alert">
          Impossibile salvare la valutazione:{' '}
          {saveSelfAssessment.error instanceof Error
            ? saveSelfAssessment.error.message
            : 'errore sconosciuto'}
        </p>
      )}

      <DataTable
        className="skill-list__table"
        rows={rows}
        columns={columns}
        rowKey={(row) => row.skillId}
        loading={loading}
        error={error ?? null}
        emptyMessage={emptyMessage}
        defaultSort={defaultSort}
        paginated={paginated}
        pageSize={pageSize}
      />

      {scoreDetailRow !== null && (
        <ScoreDetailModal
          row={scoreDetailRow}
          config={scoringConfig}
          onClose={() => setScoreDetailRow(null)}
        />
      )}

      {levelSourcesRow !== null && (
        <LevelSourcesModal
          row={levelSourcesRow}
          onClose={() => setLevelSourcesRow(null)}
        />
      )}
    </>
  );
}

export default SkillAssessmentTable;
